import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import useTranslation from 'next-translate/useTranslation';

import styles from './suggested-price-notice.module.scss';

const COPY_SCOPE = 'bid_request.line_items.suggested_price.explanation';

type SuggestedPriceNoticeTone = 'error' | 'info';

interface SuggestedPriceNoticeProps {
  /** Key under `...suggested_price.explanation` holding the message to show. */
  copyKey: string;
  dataTestId: string;
  tone: SuggestedPriceNoticeTone;
}

/**
 * Stands in wherever the explanation list would otherwise sit, when there is nothing to list.
 *
 * Two tones, same layout: `error` for a failed request — something went wrong and the buyer should
 * retry — and `info` for a successful request that simply had nothing to report, which is a
 * legitimate outcome rather than a fault. Only the error tone is announced as an alert; an
 * informational outcome should not interrupt a screen reader.
 */
export const SuggestedPriceNotice = ({ copyKey, dataTestId, tone }: SuggestedPriceNoticeProps) => {
  const { t } = useTranslation('common');
  const isError = tone === 'error';
  const Glyph = isError ? ExclamationCircleOutlined : InfoCircleOutlined;

  return (
    <p
      className={styles.notice}
      data-testid={dataTestId}
      role={isError ? 'alert' : undefined}
    >
      <Glyph aria-hidden className={isError ? styles.iconError : styles.iconInfo} />
      {t(`${COPY_SCOPE}.${copyKey}`)}
    </p>
  );
};
